import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  message,
  Popconfirm,
  Form,
  Row,
  Col,
  Switch,
  notification,
} from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  useSettingsServiceListPaymentMethods,
  useSettingsServiceDeletePaymentMethod,
  useSettingsServiceCreatePaymentMethod,
  useSettingsServiceUpdatePaymentMethod,
  useSettingsServiceListPaymentMethodsKey,
} from "api/queries";
import qs from "query-string";
import { useQueryClient } from "@tanstack/react-query";
import AccountSelect from "components/common/AccountSelect";

const PaymentMethodListing = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortQuery, setSortQuery] = useState<any>("");
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isRefetching, refetch } =
    useSettingsServiceListPaymentMethods(
      {
        pagination: qs.stringify({ limit, page }),
        filters: qs.stringify({}),
        sort: sortQuery,
        searchQuery,
      },
      [useSettingsServiceListPaymentMethodsKey]
    );

  const { mutate: deletePaymentMethod, isPending: isDeletePending } =
    useSettingsServiceDeletePaymentMethod({
      onSuccess: () => {
        message.success("Payment method deleted successfully");
        refetch();
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to delete payment method");
      },
    });

  const { mutate: createPaymentMethod, isPending: isCreating } =
    useSettingsServiceCreatePaymentMethod({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["settingsServiceListPaymentMethods"],
        });
        message.success("Payment method created successfully");
        setCreateOpen(false);
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to create payment method");
      },
    });

  const { mutate: updatePaymentMethod, isPending: isUpdating } =
    useSettingsServiceUpdatePaymentMethod({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["settingsServiceListPaymentMethods"],
        });
        message.success("Payment method updated successfully");
        setEditOpen(false);
        setSelected(null);
      },
      onError: (error: any) => {
        message.error(error?.message || "Failed to update payment method");
      },
    });

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter && sorter.field) {
      setSortQuery({
        [sorter.field]: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSortQuery("");
    }
  };

  useEffect(() => {
    refetch();
  }, [page, limit, sortQuery, searchQuery]);

  const handleCreate = (values: any) => {
    createPaymentMethod({ requestBody: values });
  };

  const handleEdit = (values: any) => {
    if (selected) {
      updatePaymentMethod({
        idPaymentMethod: (selected as any).idPaymentMethod,
        requestBody: values,
      });
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "paymentMethodName",
      key: "paymentMethodName",
      sorter: true,
    },
    {
      title: "Account",
      dataIndex: "account",
      key: "account",
      sorter: true,
      render: (account: any) => account || "-",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: any) =>
        isActive ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="red">Inactive</Tag>
        ),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_v: any, record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setSelected(record);
              setEditOpen(true);
            }}
            size="small"
          />
          <Popconfirm
            title="Are you sure?"
            description="This payment method will be deleted permanently"
            onConfirm={() =>
              deletePaymentMethod({ idPaymentMethod: record.idPaymentMethod })
            }
            okButtonProps={{ loading: isDeletePending }}
          >
            <Button icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>Payment Methods</h2>
      </div>
      <Space
        style={{
          marginBottom: 16,
          width: "100%",
          justifyContent: "space-between",
          display: "flex",
        }}
      >
        <Input.Search
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search payment methods"
          style={{ maxWidth: 300 }}
        />
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Add Payment Method
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={data?.items || []}
        loading={isLoading || isRefetching}
        rowKey={(item) => item.idPaymentMethod}
        pagination={{
          total: data?.total || 0,
          current: page,
          pageSize: limit,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "20", "30", "40", "50"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <PaymentMethodModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <PaymentMethodModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelected(null);
        }}
        defaultValues={selected}
      />
    </div>
  );
};

const PaymentMethodModal = ({
  isOpen,
  onClose,
  defaultValues,
}: {
  isOpen: boolean;
  onClose: (reload?: boolean) => void;
  defaultValues?: any;
}) => {
  const [formRef] = Form.useForm();
  const queryClient = useQueryClient();
  const isEdit = !!defaultValues;

  const { mutate: createPaymentMethod, isPending: isCreating } =
    useSettingsServiceCreatePaymentMethod({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [useSettingsServiceListPaymentMethodsKey],
        });
        message.success("Payment method created successfully");
        onClose(true);
      },
      onError: (error: any) => {
        notification.error({
          description:
            error?.body?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "An unexpected error occurred.",
          type: "error",
          message: "Failed to create payment method",
        });
      },
    });

  const { mutate: updatePaymentMethod, isPending: isUpdating } =
    useSettingsServiceUpdatePaymentMethod({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [useSettingsServiceListPaymentMethodsKey],
        });
        message.success("Payment method updated successfully");
        onClose(true);
      },
      onError: (error: any) => {
        notification.error({
          description:
            error?.body?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "An unexpected error occurred.",
          type: "error",
          message: "Failed to update payment method",
        });
      },
    });

  const handleSubmit = (values: any) => {
    if (isEdit) {
      updatePaymentMethod({
        idPaymentMethod: defaultValues.idPaymentMethod,
        requestBody: values,
      });
    } else {
      createPaymentMethod({ requestBody: values });
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isEdit) {
        formRef.setFieldsValue({
          paymentMethodName: defaultValues.paymentMethodName,
          account: defaultValues.account,
          isActive: defaultValues.isActive !== false,
        });
      } else {
        formRef.resetFields();
      }
    }
    // eslint-disable-next-line
  }, [isOpen, defaultValues]);

  return (
    <Modal
      open={isOpen}
      onCancel={() => onClose()}
      okText={isEdit ? "Update" : "Create"}
      okButtonProps={{
        icon: isEdit ? <EditOutlined /> : <PlusCircleOutlined />,
        loading: isCreating || isUpdating,
      }}
      cancelButtonProps={{ disabled: isCreating || isUpdating }}
      onOk={formRef.submit}
      title={isEdit ? "Edit Payment Method" : "Add Payment Method"}
      destroyOnClose
    >
      <Form form={formRef} onFinish={handleSubmit} layout="vertical">
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              hasFeedback
              label="Payment Method Name"
              name="paymentMethodName"
              rules={[
                { required: true, message: "Payment method name is required" },
              ]}
            >
              <Input placeholder="Payment method name" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              hasFeedback
              label="Account"
              name="account"
              rules={[{ required: true, message: "Account is required" }]}
            >
              <AccountSelect isForm={true} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              label="Active"
              name="isActive"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default PaymentMethodListing;
